import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchHomeContent,
  fetchTeams,
  fetchNews,
  fetchPartners,
  fetchHighlightedWork,
  sortHighlightedWork,
} from './homeApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_ARRAY = [];

// ---------------------------------------------------------------------------
// useHomeContent
// ---------------------------------------------------------------------------

export const useHomeContent = (content) => {
  const dispatch = useDispatch();

  const homeContent = useSelector(
    (state) => state.content.subrequests?.homeContent?.data,
  );
  const homeContentLoaded = useSelector(
    (state) => state.content.subrequests?.homeContent?.loaded,
  );

  useEffect(() => {
    if (!homeContentLoaded && !homeContent) {
      fetchHomeContent(dispatch);
    }
  }, [dispatch, homeContent, homeContentLoaded]);

  return {
    homeContent: homeContent || content,
    loaded: homeContentLoaded || false,
  };
};

// ---------------------------------------------------------------------------
// useHomeTeams
// ---------------------------------------------------------------------------
export const useHomeTeams = () => {
  const dispatch = useDispatch();

  const items = useSelector(
    (state) => state.search.subrequests?.homeOurTeams?.items || EMPTY_ARRAY,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.homeOurTeams?.loaded || false,
  );

  useEffect(() => {
    if (!loaded) {
      fetchTeams(dispatch);
    }
  }, [dispatch, loaded]);

  return { items, loaded };
};

// ---------------------------------------------------------------------------
// useHomeHighlightedWork
// ---------------------------------------------------------------------------

export const useHomeHighlightedWork = (highlightedWork) => {
  const dispatch = useDispatch();

  const items = useSelector(
    (state) =>
      state.search.subrequests?.homeHighlightedWork?.items || EMPTY_ARRAY,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.homeHighlightedWork?.loaded || false,
  );

  useEffect(() => {
    if (!loaded) {
      fetchHighlightedWork(dispatch, highlightedWork);
    }
  }, [dispatch, highlightedWork, loaded]);

  const sorted = useMemo(
    () => sortHighlightedWork(items, highlightedWork),
    [items, highlightedWork],
  );

  return { items: sorted, loaded };
};

// ---------------------------------------------------------------------------
// useHomeNews
// ---------------------------------------------------------------------------
export const useHomeNews = () => {
  const dispatch = useDispatch();

  const items = useSelector(
    (state) => state.search.subrequests?.homeLatestNews?.items || EMPTY_ARRAY,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.homeLatestNews?.loaded || false,
  );

  useEffect(() => {
    if (!loaded) {
      fetchNews(dispatch);
    }
  }, [dispatch, loaded]);

  return { items, loaded };
};

// ---------------------------------------------------------------------------
// useHomePartners
// ---------------------------------------------------------------------------

export const useHomePartners = () => {
  const dispatch = useDispatch();

  const data = useSelector(
    (state) => state.content.subrequests?.homePartners?.data,
  );

  const items = data?.items || EMPTY_ARRAY;

  const loaded = useSelector(
    (state) => state.content.subrequests?.homePartners?.loaded || false,
  );

  useEffect(() => {
    if (!loaded) {
      fetchPartners(dispatch);
    }
  }, [dispatch, loaded]);

  return { items, loaded };
};